import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, Image, TouchableOpacity, Modal, TextInput, Alert, Animated, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import genericProfilePicture from '../../assets/generic.jpg';

const YearbookMessages = ({ 
  yearBookMessages, 
  formatDate, 
  cardStyle, 
  isMobile,
  onHideMessage,
  onDeleteMessage,
  onReplyMessage,
  isPageOwner = true,
  currentUserUID
}) => {
  const colors = {
    primary: '#4c956c',
    cream: '#fefde2',
    creamLight: 'rgba(254, 253, 226, 0.95)',
    text: '#1a3a2a',
    textMuted: '#5a6b63',
    border: 'rgba(76, 149, 108, 0.15)',
    danger: '#dc3545',
    warning: '#f59e0b',
    purple: '#8b5cf6'
  };

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;

  // Entrance animations
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        friction: 8,
        tension: 40,
        useNativeDriver: true
      })
    ]).start();
  }, []);

  // State for modals
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [hoveredMessage, setHoveredMessage] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [messageToReply, setMessageToReply] = useState(null);
  const [replyText, setReplyText] = useState('');

  const handleHideMessage = (msg) => {
    if (onHideMessage) {
      onHideMessage(msg);
    }
  };

  const handleDeleteClick = (msg) => {
    setMessageToDelete(msg);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDeleteMessage && messageToDelete) {
      onDeleteMessage(messageToDelete);
    }
    setShowDeleteConfirm(false);
    setMessageToDelete(null);
  };

  const handleReplyClick = (msg) => {
    setMessageToReply(msg);
    setReplyText('');
    setShowReplyModal(true);
  };

  const handleSendReply = () => {
    if (onReplyMessage && messageToReply && replyText.trim()) {
      onReplyMessage(messageToReply, replyText.trim());
    }
    setShowReplyModal(false);
    setMessageToReply(null);
    setReplyText('');
  };

  // Generate a color based on name for avatar backgrounds
  const getAvatarColor = (name) => {
    const colorOptions = ['#4c956c', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'];
    const index = (name || 'A').charCodeAt(0) % colorOptions.length;
    return colorOptions[index];
  };

  // Format timestamp - handles Firestore timestamps and regular dates
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      // Handle Firestore timestamp object
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
      // Handle regular date string or Date object
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return 'Recently';
    }
  };

  return (
    <Animated.View style={[
      cardStyle, 
      { 
        padding: isMobile ? 20 : 28, 
        overflow: 'hidden', 
        position: 'relative', 
        display: 'flex', 
        flexDirection: 'column',
        opacity: fadeAnim,
        transform: [
          { translateY: slideAnim },
          { scale: scaleAnim }
        ]
      }
    ]}>
      {/* Decorative Bubbles */}
      <Animated.View style={{
        position: 'absolute',
        top: -20,
        right: 40,
        width: 70,
        height: 70,
        borderRadius: 35,
        backgroundColor: 'rgba(139, 92, 246, 0.06)',
        opacity: fadeAnim
      }} />
      <Animated.View style={{
        position: 'absolute',
        bottom: 20,
        right: -20,
        width: 50,
        height: 50,
        borderRadius: 25,
        backgroundColor: 'rgba(139, 92, 246, 0.04)'
      }} />
      
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <View>
          <Text style={{
            fontSize: isMobile ? 18 : 22,
            fontWeight: '700',
            color: colors.text,
            fontFamily: 'Bodoni72Smallcaps'
          }}>
            Yearbook Messages
          </Text>
          <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
            Notes from friends & family
          </Text>
        </View>
        
        {yearBookMessages.length > 0 && (
          <View style={{
            backgroundColor: '#8b5cf6',
            paddingHorizontal: 12,
            paddingVertical: 6,
            borderRadius: 16
          }}>
            <Text style={{ fontSize: 14, fontWeight: '700', color: '#fff' }}>
              {yearBookMessages.length}
            </Text>
          </View>
        )}
      </View>
      
      {yearBookMessages.length === 0 ? (
        <View style={{
          padding: 40,
          alignItems: 'center',
          backgroundColor: 'rgba(139, 92, 246, 0.08)',
          borderRadius: 16,
          borderWidth: 2,
          borderColor: 'rgba(139, 92, 246, 0.15)',
          borderStyle: 'dashed',
          minHeight: isMobile ? 220 : 320,
          justifyContent: 'center'
        }}>
          <View style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            backgroundColor: 'rgba(139, 92, 246, 0.15)',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16
          }}>
            <Ionicons name="chatbubbles-outline" size={32} color="#8b5cf6" />
          </View>
          <Text style={{ fontSize: 18, fontWeight: 'bold', color: colors.text, marginBottom: 8 }}>
            No messages yet
          </Text>
          <Text style={{ fontSize: 14, color: colors.textMuted, textAlign: 'center', maxWidth: 280 }}>
            Share your profile link with friends and family so they can sign your digital yearbook!
          </Text>
        </View>
      ) : (
        <View style={{ maxHeight: isMobile ? 280 : 420 }}>
          <ScrollView 
            showsVerticalScrollIndicator={true}
            contentContainerStyle={{ paddingBottom: 10 }}
          >
          {(() => {
            // Group replies under their parent messages (iMessage-style threading)
            const parentMessages = yearBookMessages.filter(m => !m.isReply);
            const replyMap = {};
            yearBookMessages.filter(m => m.isReply).forEach(reply => {
              let parentKey = '__orphan';
              for (let i = 0; i < parentMessages.length; i++) {
                const p = parentMessages[i];
                if (reply.replyToId && p.id && reply.replyToId === p.id) {
                  parentKey = p.id || i;
                  break;
                }
                const pText = (p.text || p.message || p.content || '').trim();
                const rText = (reply.originalMessageText || '').trim();
                if (rText && pText && pText === rText) {
                  parentKey = p.id || i;
                  break;
                }
              }
              if (!replyMap[parentKey]) replyMap[parentKey] = [];
              replyMap[parentKey].push(reply);
            });
            const orphanReplies = replyMap['__orphan'] || [];
            const allToRender = [...parentMessages, ...orphanReplies];

            const renderMsg = (msg, index, isReplyMsg = false) => {
            const messageText = msg.text || msg.message || msg.content || '';
            const senderName = msg.senderFirstName 
              ? `${msg.senderFirstName} ${msg.senderLastName || ''}`.trim() 
              : 'Anonymous';
            const avatarColor = getAvatarColor(senderName);
            const photoURL = msg.senderPhotoURL || msg.profilePictureURL || null;
            const isHidden = msg.hidden;
            const isHovered = hoveredMessage === (msg.id || index);
            
            if (isHidden && !isPageOwner) return null;
            
            return (
              <View key={msg.id || index}>
                {isReplyMsg && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 14, marginBottom: 1, marginTop: 2 }}>
                    {/* Curved reply connector */}
                    <View style={{ width: 20, height: 24, marginRight: 4 }}>
                      <div style={{
                        width: 20,
                        height: 24,
                        borderLeft: '2px solid rgba(76,149,108,0.35)',
                        borderTop: '2px solid rgba(76,149,108,0.35)',
                        borderTopLeftRadius: 12,
                      }} />
                    </View>
                    <Text style={{ fontSize: 11, color: '#4c956c', fontStyle: 'italic' }}>Replying to {msg.originalSenderName || 'someone'}</Text>
                  </View>
                )}
              <View 
                onMouseEnter={() => setHoveredMessage(msg.id || index)}
                onMouseLeave={() => setHoveredMessage(null)}
                style={{
                  backgroundColor: isHidden ? 'rgba(255,255,255,0.5)' : '#fff',
                  borderRadius: 12,
                  padding: isMobile ? 12 : 14,
                  marginBottom: isReplyMsg ? 4 : 8,
                  marginLeft: isReplyMsg ? 20 : 0,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 1 },
                  shadowOpacity: 0.04,
                  shadowRadius: 4,
                  elevation: 1,
                  borderLeftWidth: 3,
                  borderLeftColor: isReplyMsg ? 'rgba(76,149,108,0.4)' : (isHidden ? colors.textMuted : avatarColor),
                  opacity: isHidden ? 0.6 : 1
                }}
              >
                {/* Message Header - More Compact */}
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 8
                }}>
                  {photoURL ? (
                    <Image 
                      source={{ uri: photoURL }} 
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        marginRight: 10
                      }}
                      defaultSource={genericProfilePicture}
                    />
                  ) : (
                    <View style={{
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: avatarColor,
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 10
                    }}>
                      <Text style={{
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: 13
                      }}>
                        {senderName.charAt(0).toUpperCase()}
                      </Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: colors.text
                      }}>
                        {senderName}
                      </Text>
                      {isHidden && (
                        <View style={{ 
                          backgroundColor: 'rgba(0,0,0,0.1)', 
                          paddingHorizontal: 6, 
                          paddingVertical: 2, 
                          borderRadius: 4 
                        }}>
                          <Text style={{ fontSize: 10, color: colors.textMuted }}>Hidden</Text>
                        </View>
                      )}
                    </View>
                    <Text style={{
                      fontSize: 11,
                      color: colors.textMuted
                    }}>
                      {formatTimestamp(msg.timestamp)}
                    </Text>
                  </View>
                  
                  {/* Action Buttons - Show on hover or always on mobile */}
                  {isPageOwner && (isHovered || isMobile) && (
                    <View style={{ flexDirection: 'row', gap: 4 }}>
                      {/* Reply */}
                      {onReplyMessage && (
                        <TouchableOpacity
                          onPress={() => handleReplyClick(msg)}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 14,
                            backgroundColor: 'rgba(76, 149, 108, 0.1)',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Ionicons name="chatbubble-outline" size={14} color={colors.primary} />
                        </TouchableOpacity>
                      )}
                      {/* Hide/Unhide */}
                      {onHideMessage && (
                        <TouchableOpacity
                          onPress={() => handleHideMessage(msg)}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 14,
                            backgroundColor: isHidden ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Ionicons 
                            name={isHidden ? 'eye' : 'eye-off'} 
                            size={14} 
                            color={isHidden ? '#22c55e' : colors.warning} 
                          />
                        </TouchableOpacity>
                      )}
                      {/* Delete */}
                      {onDeleteMessage && (
                        <TouchableOpacity
                          onPress={() => handleDeleteClick(msg)}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 14,
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Ionicons name="trash-outline" size={14} color={colors.danger} />
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
                
                {/* Message Image if exists - Smaller */}
                {msg.image && (
                  <Image 
                    source={{ uri: msg.image }}
                    style={{
                      width: '100%',
                      height: 100,
                      borderRadius: 8,
                      marginBottom: 8
                    }}
                    resizeMode="cover"
                  />
                )}
                
                {/* Message Content - More Compact */}
                {messageText ? (
                  <Text style={{
                    fontSize: 13,
                    color: '#374151',
                    lineHeight: 19
                  }} numberOfLines={3}>
                    {messageText}
                  </Text>
                ) : null}
              </View>
              </View>
            );
            };

            return allToRender.map((msg, index) => {
              const replies = replyMap[msg.id || index] || [];
              return (
                <View key={msg.id || `thread-${index}`}>
                  {renderMsg(msg, index, false)}
                  {replies.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)).map((reply, rIdx) => renderMsg(reply, `reply-${index}-${rIdx}`, true))}
                </View>
              );
            });
          })()}
        </ScrollView>
        </View>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteConfirm(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
          <View style={{
            backgroundColor: colors.creamLight,
            borderRadius: 20,
            padding: 24,
            width: '100%',
            maxWidth: 380
          }}>
            <View style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'center',
              marginBottom: 16
            }}>
              <Ionicons name="trash" size={28} color={colors.danger} />
            </View>
            <Text style={{ 
              fontSize: 20, 
              fontWeight: '700', 
              color: colors.text, 
              textAlign: 'center',
              marginBottom: 8
            }}>
              Delete Message?
            </Text>
            <Text style={{ 
              fontSize: 14, 
              color: colors.textMuted, 
              textAlign: 'center',
              marginBottom: 24
            }}>
              This will permanently remove this yearbook message. This action cannot be undone.
            </Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity
                onPress={() => setShowDeleteConfirm(false)}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: '#f0f0f0',
                  alignItems: 'center'
                }}
              >
                <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={confirmDelete}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: colors.danger,
                  alignItems: 'center'
                }}
              >
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#fff' }}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Reply Modal */}
      <Modal
        visible={showReplyModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowReplyModal(false)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
          <View style={{
            backgroundColor: colors.creamLight,
            borderRadius: 20,
            padding: 24,
            width: '100%',
            maxWidth: 420
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text }}>
                Reply to Message
              </Text>
              <TouchableOpacity onPress={() => setShowReplyModal(false)}>
                <Ionicons name="close" size={24} color={colors.textMuted} />
              </TouchableOpacity>
            </View>
            
            {messageToReply && (
              <View style={{ 
                backgroundColor: 'rgba(0,0,0,0.04)', 
                padding: 12, 
                borderRadius: 10, 
                marginBottom: 16,
                borderLeftWidth: 3,
                borderLeftColor: colors.purple
              }}>
                <Text style={{ fontSize: 13, fontWeight: '600', color: colors.text, marginBottom: 4 }}>
                  {messageToReply.senderFirstName} {messageToReply.senderLastName || ''}
                </Text>
                <Text style={{ fontSize: 12, color: colors.textMuted }} numberOfLines={2}>
                  {messageToReply.text || messageToReply.message || messageToReply.content}
                </Text>
              </View>
            )}
            
            <TextInput
              value={replyText}
              onChangeText={setReplyText}
              placeholder="Write your reply..."
              placeholderTextColor={colors.textMuted}
              multiline
              numberOfLines={4}
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                padding: 14,
                fontSize: 15,
                color: colors.text,
                borderWidth: 1,
                borderColor: colors.border,
                minHeight: 100,
                textAlignVertical: 'top',
                marginBottom: 16
              }}
            />
            
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity
                onPress={() => setShowReplyModal(false)}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: '#f0f0f0',
                  alignItems: 'center'
                }}
              >
                <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSendReply}
                disabled={!replyText.trim()}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: replyText.trim() ? colors.primary : '#ccc',
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 6
                }}
              >
                <Ionicons name="send" size={16} color="#fff" />
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#fff' }}>Send</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </Animated.View>
  );
};

export default YearbookMessages;
